import React from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';

// @material-ui/core components
import withStyles from '@material-ui/core/styles/withStyles';
import Paper from '@material-ui/core/Paper';
import Breadcrumbs from '@material-ui/lab/Breadcrumbs';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';

// @material-ui/icons
import VpnKey from '@material-ui/icons/VpnKey';
import Refresh from '@material-ui/icons/Refresh';
import CheckCircle from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import Warning from '@material-ui/icons/Warning';
import ReplyIcon from '@material-ui/icons/Reply';
import Alert from '@material-ui/icons/NotificationImportant';
import Save from '@material-ui/icons/Save';
import Info from '@material-ui/icons/Info';

// core components
import GridContainer from '../../../components/Grid/GridContainer.jsx';
import GridItem from '../../../components/Grid/GridItem.jsx';
import Card from '../../../components/Card/Card.jsx';
import CardHeader from '../../../components/Card/CardHeader.jsx';
import CardIcon from '../../../components/Card/CardIcon.jsx';
import CardBody from '../../../components/Card/CardBody.jsx';
import Button from '../../../components/CustomButtons/Button.jsx';
import Snackbar from '../../../components/Snackbar/Snackbar.jsx';

import {
  fetchProtheusStatus,
  updateProtheusToken,
  refreshProtheusToken,
  resetProtheusInfo,
} from '../../../actions/protheusAction';
import { formatDateTimeBr } from '../../../helpers/utils';

const styles = (theme) => ({
  paperBreadcrumb: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
    marginBottom: '16px',
  },
  icon: {
    fontSize: '14px',
    marginRight: '4px',
  },
  statusCard: {
    padding: '16px',
    borderRadius: '6px',
    marginBottom: '20px',
  },
  statusValid: {
    backgroundColor: '#e8f5e9',
    borderLeft: '5px solid #4caf50',
    color: '#2e7d32',
  },
  statusError: {
    backgroundColor: '#ffebee',
    borderLeft: '5px solid #f44336',
    color: '#c62828',
  },
  statusWarning: {
    backgroundColor: '#fff8e1',
    borderLeft: '5px solid #ff9800',
    color: '#ef6c00',
  },
  statusTitle: {
    fontWeight: 'bold',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  statusDetail: {
    fontSize: '14px',
    marginTop: '4px',
  },
  textareaInput: {
    marginTop: '16px',
    marginBottom: '16px',
  },
  actionButtons: {
    marginTop: '20px',
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  helpBox: {
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '4px',
    marginTop: '20px',
    fontSize: '13px',
    color: '#555',
  },
});

class ProtheusTokenPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      accessToken: '',
      refreshToken: '',
      isSubmitting: false,
      isRefreshing: false,
      notificationMessage: '',
      notificationColor: 'primary',
      showNotification: false,
    };
  }

  componentDidMount() {
    this.props.fetchProtheusStatus();
  }

  componentWillUnmount() {
    this.props.resetProtheusInfo();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.updateTokenSuccess &&
      prevProps.updateTokenSuccess !== this.props.updateTokenSuccess
    ) {
      this.setState(
        {
          notificationMessage: this.props.updateTokenSuccess,
          notificationColor: 'success',
          showNotification: true,
          accessToken: '',
          refreshToken: '',
          isSubmitting: false,
        },
        () => {
          this.props.fetchProtheusStatus();
        }
      );
    }

    if (
      this.props.updateTokenError &&
      prevProps.updateTokenError !== this.props.updateTokenError
    ) {
      this.setState({
        notificationMessage: this.props.updateTokenError,
        notificationColor: 'danger',
        showNotification: true,
        isSubmitting: false,
      });
    }

    if (
      this.props.refreshTokenSuccess &&
      prevProps.refreshTokenSuccess !== this.props.refreshTokenSuccess
    ) {
      this.setState(
        {
          notificationMessage: this.props.refreshTokenSuccess,
          notificationColor: 'success',
          showNotification: true,
          isRefreshing: false,
        },
        () => {
          this.props.fetchProtheusStatus();
        }
      );
    }

    if (
      this.props.refreshTokenError &&
      prevProps.refreshTokenError !== this.props.refreshTokenError
    ) {
      this.setState({
        notificationMessage: this.props.refreshTokenError,
        notificationColor: 'danger',
        showNotification: true,
        isRefreshing: false,
      });
    }
  }

  handleSaveTokens = () => {
    const { accessToken, refreshToken } = this.state;

    if (!accessToken.trim() || !refreshToken.trim()) {
      this.setState({
        notificationMessage: 'Preencha o Access Token e o Refresh Token.',
        notificationColor: 'warning',
        showNotification: true,
      });
      return;
    }

    this.setState({ isSubmitting: true });
    this.props.updateProtheusToken(accessToken.trim(), refreshToken.trim());
  };

  handleTestRefresh = () => {
    this.setState({ isRefreshing: true });
    this.props.refreshProtheusToken();
  };

  closeNotification = () => {
    this.setState({ showNotification: false, notificationMessage: '' });
  };

  renderStatusBanner() {
    const { classes, protheusStatus } = this.props;

    if (!protheusStatus) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <CircularProgress size={30} />
          <Typography variant="body2" style={{ marginTop: '8px' }}>
            Consultando status da conexão com a API Protheus...
          </Typography>
        </div>
      );
    }

    const isValid = protheusStatus.is_valid;
    const isExpired = protheusStatus.is_expired;
    const hasError = protheusStatus.status === 'refresh_failed' || isExpired || !isValid;

    let bannerClass = classes.statusValid;
    let icon = <CheckCircle style={{ marginRight: '8px', color: '#4caf50' }} />;
    let title = 'Conexão Ativa - Token Válido';

    if (hasError) {
      bannerClass = classes.statusError;
      icon = <ErrorIcon style={{ marginRight: '8px', color: '#f44336' }} />;
      title = isExpired
        ? 'Atenção: O Token do Protheus está Expirado'
        : 'Atenção: Falha de Autenticação / Renovação no Protheus';
    } else if (protheusStatus.is_expiring_soon) {
      bannerClass = classes.statusWarning;
      icon = <Warning style={{ marginRight: '8px', color: '#ff9800' }} />;
      title = 'Aviso: Token Próximo da Expiração (Menos de 2 horas)';
    }

    return (
      <div className={`${classes.statusCard} ${bannerClass}`}>
        <div className={classes.statusTitle}>
          {icon}
          <span>{title}</span>
          <Chip
            size="small"
            label={protheusStatus.source === 'manual' ? 'Origem: Manual (Interface)' : 'Origem: .env'}
            style={{ marginLeft: 'auto', fontSize: '11px' }}
          />
        </div>

        {protheusStatus.error_message && (
          <div className={classes.statusDetail} style={{ fontWeight: '500' }}>
            <strong>Detalhe do Erro:</strong> {protheusStatus.error_message}
          </div>
        )}

        <GridContainer style={{ marginTop: '8px' }}>
          <GridItem xs={12} sm={6}>
            <div className={classes.statusDetail}>
              <strong>Expiração do Access Token:</strong>{' '}
              {formatDateTimeBr(protheusStatus.expires_at) || 'Não identificada'}
            </div>
          </GridItem>
          <GridItem xs={12} sm={6}>
            <div className={classes.statusDetail}>
              <strong>Última Renovação bem-sucedida:</strong>{' '}
              {formatDateTimeBr(protheusStatus.last_refreshed_at) || 'Nunca'}
            </div>
          </GridItem>
          {protheusStatus.refresh_token_preview && (
            <GridItem xs={12}>
              <div className={classes.statusDetail} style={{ color: '#666', fontSize: '12px', marginTop: '6px' }}>
                <strong>Refresh Token em uso:</strong> <code>{protheusStatus.refresh_token_preview}</code>
              </div>
            </GridItem>
          )}
        </GridContainer>
      </div>
    );
  }

  render() {
    const { classes } = this.props;

    let notification = (
      <Snackbar
        place="br"
        color={this.state.notificationColor}
        icon={Alert}
        message={this.state.notificationMessage}
        open={this.state.showNotification}
        closeNotification={this.closeNotification}
        close
      />
    );

    return (
      <div>
        <GridContainer>
          <GridItem xs={12}>
            <Paper className={classes.paperBreadcrumb}>
              <Breadcrumbs separator="›" arial-label="Breadcrumb">
                <Link color="inherit" href="/dashboard">
                  <ReplyIcon className={classes.icon} />
                  Dashboard
                </Link>
                <Typography color="textPrimary">Integração Protheus</Typography>
              </Breadcrumbs>
            </Paper>
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12}>
            {this.renderStatusBanner()}
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} md={8}>
            <Card>
              <CardHeader color="primary" icon>
                <CardIcon color="primary">
                  <VpnKey />
                </CardIcon>
                <h4 style={{ margin: '8px 0 0', fontWeight: 'bold' }}>
                  Atualizar Tokens da API Protheus
                </h4>
                <p style={{ margin: 0, color: '#777', fontSize: '13px' }}>
                  Insira o novo par de tokens (Access e Refresh) gerados no Protheus. A alteração é salva no banco e entra em vigor imediatamente, sem reiniciar containers.
                </p>
              </CardHeader>
              <CardBody>
                <TextField
                  id="protheus-access-token"
                  label="Access Token (JWT)"
                  placeholder="Cole aqui o token de acesso (Bearer token gerado no Protheus)..."
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  value={this.state.accessToken}
                  onChange={(e) => this.setState({ accessToken: e.target.value })}
                  className={classes.textareaInput}
                />

                <TextField
                  id="protheus-refresh-token"
                  label="Refresh Token"
                  placeholder="Cole aqui o refresh token gerado no Protheus..."
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  value={this.state.refreshToken}
                  onChange={(e) => this.setState({ refreshToken: e.target.value })}
                  className={classes.textareaInput}
                />

                <div className={classes.actionButtons}>
                  <Button
                    color="success"
                    onClick={this.handleSaveTokens}
                    disabled={this.state.isSubmitting}
                  >
                    {this.state.isSubmitting ? (
                      <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} />
                    ) : (
                      <Save style={{ marginRight: '6px' }} />
                    )}
                    Salvar e Aplicar Tokens
                  </Button>

                  <Button
                    color="info"
                    simple
                    onClick={this.handleTestRefresh}
                    disabled={this.state.isRefreshing}
                  >
                    {this.state.isRefreshing ? (
                      <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} />
                    ) : (
                      <Refresh style={{ marginRight: '6px' }} />
                    )}
                    Testar Renovação (Refresh)
                  </Button>

                  <Button
                    color="primary"
                    simple
                    onClick={() => this.props.fetchProtheusStatus()}
                  >
                    <Refresh style={{ marginRight: '6px' }} />
                    Recarregar Status
                  </Button>
                </div>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem xs={12} md={4}>
            <Card>
              <CardHeader color="info" icon>
                <CardIcon color="info">
                  <Info />
                </CardIcon>
                <h4 style={{ margin: '8px 0 0', fontWeight: 'bold' }}>
                  Instruções & Dicas
                </h4>
              </CardHeader>
              <CardBody>
                <Typography variant="body2" paragraph>
                  <strong>Como funciona a renovação?</strong>
                </Typography>
                <Typography variant="caption" paragraph color="textSecondary">
                  O sistema possui uma rotina em segundo plano que executa a cada 4 horas para renovar o Access Token antes que ele expire, utilizando o <em>Refresh Token</em>.
                </Typography>

                <Typography variant="body2" paragraph>
                  <strong>Quando é necessário atualizar aqui?</strong>
                </Typography>
                <Typography variant="caption" paragraph color="textSecondary">
                  Quando o <em>Refresh Token</em> atinge sua validade máxima ou quando a senha/usuário de integração no Protheus for alterada, o Protheus retorna <code>invalid_grant</code>.
                </Typography>

                <div className={classes.helpBox}>
                  <strong>Passos para obter novos tokens:</strong>
                  <ol style={{ paddingLeft: '18px', margin: '8px 0 0' }}>
                    <li>Faça login na API de Autenticação do TOTVS Protheus (ex: via Postman).</li>
                    <li>Copie o <code>access_token</code> e o <code>refresh_token</code> da resposta.</li>
                    <li>Cole nos campos ao lado e clique em <strong>Salvar e Aplicar</strong>.</li>
                  </ol>
                </div>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>

        {notification}
      </div>
    );
  }
}

ProtheusTokenPage.propTypes = {
  classes: PropTypes.object.isRequired,
  protheusStatus: PropTypes.object,
  updateTokenSuccess: PropTypes.string,
  updateTokenError: PropTypes.string,
  refreshTokenSuccess: PropTypes.string,
  refreshTokenError: PropTypes.string,
  fetchProtheusStatus: PropTypes.func.isRequired,
  updateProtheusToken: PropTypes.func.isRequired,
  refreshProtheusToken: PropTypes.func.isRequired,
  resetProtheusInfo: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    protheusStatus: state.protheusReducer.protheusStatus,
    updateTokenSuccess: state.protheusReducer.updateTokenSuccess,
    updateTokenError: state.protheusReducer.updateTokenError,
    refreshTokenSuccess: state.protheusReducer.refreshTokenSuccess,
    refreshTokenError: state.protheusReducer.refreshTokenError,
  };
}

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    fetchProtheusStatus,
    updateProtheusToken,
    refreshProtheusToken,
    resetProtheusInfo,
  })
)(withRouter(ProtheusTokenPage));
